import merge from 'deepmerge';

import { setJLBasketData } from './setJLBasketData';

const overwriteMerge = (_dest, source) => source;

export const isCompareEvent = (analytics) =>
  Boolean(
    analytics?.data?.compare?.product &&
      analytics.data.compare.product.length > 0
  );

export const getPageType = (
  type,
  isComparing = false,
  { totalResults = 0 } = {}
) => {
  if (isComparing) {
    return 'comparison overlay';
  }

  switch (type) {
    case 'search': {
      if (totalResults > 0) {
        return 'search results';
      }
      return 'no search results';
    }
    case 'brand':
    case 'browse': {
      return 'product list page grid';
    }
    default: {
      return 'unknown';
    }
  }
};

export const getPageName = (
  type,
  isComparing = false,
  { breadcrumbs } = {}
) => {
  if (isComparing) {
    return 'jl:comparison page';
  }

  switch (type) {
    case 'search': {
      return 'jl:search';
    }
    case 'brand':
    case 'browse': {
      const displayNames =
        breadcrumbs && breadcrumbs.length > 0
          ? breadcrumbs.map(({ displayName }) => displayName)
          : [];
      const crumbs = displayNames.join(':').toLowerCase();
      return `jl:${crumbs}:grid`;
    }
    default: {
      return 'jl:unknown';
    }
  }
};

export const getBreadcrumbData = (breadcrumbs) =>
  breadcrumbs && breadcrumbs.length > 0
    ? breadcrumbs.map((crumb) => ({
        value: crumb.displayName,
        clickable: crumb.clickable ? 1 : 0,
      }))
    : [];

export const stripTagsFromSearch = (searchTerm) => {
  if (!searchTerm) return '';

  const tags = ['#_search_suggestion_referral'];
  let cleanSearchTerm = searchTerm;

  tags.forEach((tag) => {
    cleanSearchTerm = searchTerm.replace(tag, '');
  });

  return cleanSearchTerm;
};

export const getSearchData = (search, breadcrumbs) => {
  let searchTerms = stripTagsFromSearch(search.term);

  if (
    searchTerms.length === 0 &&
    search.isSuggestionLink &&
    breadcrumbs?.length > 0
  ) {
    searchTerms = breadcrumbs[breadcrumbs.length - 1].displayName;
  }

  return {
    searchMethod: search?.alsoSearched ? 'Also searched' : 'typed search',
    sort: search?.sortBy || 'default: relevance',
    pageNum: `page ${search.page}`,
    searchTerms,
    preSelectedFilter: '',
  };
};

export const getPageSearch = (type, analytics, search, breadcrumbs) => {
  if (type === 'search' || search.isSuggestionLink) {
    return {
      page: {
        search: {
          filter: analytics?.data?.filter,
          filterNvals: analytics?.data?.filterNvals,
          ...analytics?.data?.search,
          ...getSearchData(search, breadcrumbs, type),
        },
      },
    };
  }

  const { originalSearchTerm } = search;
  if (originalSearchTerm) {
    return {
      page: {
        search: {
          searchMethod: 'search redirect',
          searchTerms: stripTagsFromSearch(
            decodeURIComponent(originalSearchTerm)
          ),
        },
      },
    };
  }
  return {};
};

export const setJLData = (breadcrumbs, analytics, search, page) => {
  const isComparing = isCompareEvent(analytics);

  const totalResults = analytics?.data?.search?.totalNumberOfResults;

  const { type } = page;

  const basketData = {
    basket: setJLBasketData(),
  };

  const pageData = {
    page: {
      locale: {
        country: 'uk',
        currency: 'gbp',
        language: 'en-gb',
      },
      pageInfo: {
        pageName: getPageName(type, isComparing, { breadcrumbs }),
        pageType: getPageType(type, isComparing, { totalResults }),
        pageCanonicalURL: window.location.href,
        site: 'm.johnlewis.com',
      },
      productPages: {
        products: analytics?.data?.products,
      },
    },
  };

  let brandData = {};

  if (type === 'brand') {
    const { displayName } =
      breadcrumbs.length > 0
        ? breadcrumbs[breadcrumbs.length - 1]
        : { displayName: '' };

    const brandName = displayName.toLowerCase();

    brandData = {
      page: {
        productPages: {
          products: {
            brand: brandName,
          },
        },
      },
    };
  }

  if (type === 'brand' || type === 'browse') {
    const { sort, pageNum } = getSearchData(search, breadcrumbs);
    pageData.page.productPages.products.sort = sort;
    pageData.page.productPages.products.pageNum = pageNum;
    pageData.page.productPages.products.filter = analytics?.data?.filter;
    pageData.page.productPages.products.filterNvals =
      analytics?.data?.filterNvals;
  }

  const breadcrumbData = {
    page: {
      pageInfo: {
        breadCrumb: getBreadcrumbData(breadcrumbs),
      },
    },
  };

  let compareData = {};
  if (analytics?.data?.compare) {
    compareData = {
      compare: analytics.data.compare,
    };
  }

  const searchData = getPageSearch(type, analytics, search, breadcrumbs);

  let sponsoredProducts = {};
  if (analytics?.data?.sponsoredProducts?.length > 0) {
    sponsoredProducts = {
      citrusProductAds: analytics.data.sponsoredProducts.filter(
        (ad) => ad.type === 'sponsoredProduct'
      ),
    };
  }

  const hasVertexData =
    analytics?.data?.vertex && Object.keys(analytics.data.vertex).length > 0;
  const vertexData = hasVertexData
    ? {
        vertex: {
          vertexAttributionToken: analytics.data.vertex.attributionToken,
          vertexFilter: analytics.data.vertex.filter,
          vertexProductOffset: analytics.data.vertex.vertexProductOffset,
        },
      }
    : {};

  const components = [
    basketData,
    searchData,
    pageData,
    breadcrumbData,
    brandData,
    compareData,
    sponsoredProducts,
    vertexData,
  ];

  window.jlData = merge.all([window.jlData || {}, ...components], {
    arrayMerge: overwriteMerge,
  });

  // merge doesn't overwrite if source object is empty so this removes any sponsoredProduct ads that should no longer be there
  if (
    !sponsoredProducts.citrusProductAds?.length &&
    window.jlData?.citrusProductAds
  ) {
    delete window.jlData.citrusProductAds;
  }

  if (!hasVertexData && window.jlData?.vertex) {
    delete window.jlData.vertex;
  }

  return window.jlData;
};
